import { useState } from 'react';
import './SPAdvancedCriteriaPopup.css';
import { Criteria } from '../../../../types/screener.type';
import ScreenerCriteriaSelect from '../ScreenerCriteriaSelect/ScreenerCriteriaSelect';
import { AC_GROUPS } from '../../../../constants/screener/AC.types.constant';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { ReactComponent as ArrowLeftIcon } from '../../../../assets/icons/arrow-left-s-line.svg';
import { ReactComponent as ExpandIcon } from '../../../../assets/icons/arrow-down.svg';
import { ReactComponent as CheckboxBlankIcon } from '../../../../assets/icons/B-Checkbox.svg';
import { ReactComponent as CheckboxIcon } from '../../../../assets/icons/B-Checkbox-fill.svg';
import { ReactComponent as CheckboxIndeterminateIcon } from '../../../../assets/icons/B-Checkbox-indeterminate.svg';
import CriteriaDetailList from '../CriteriaDetailList/CriteriaDetailList';
import { SectorCriteriaItem } from '../SectorCriteria/SectorCriteria';

export interface SPAdvancedCriteriaPopupProps {
  themeColor: string;
  region: string;
  sector: string;
  tempCriteria: Criteria[];
  setTempCriteria: any;
  setOpenSPAdvancedCriteria: (openSPAdvancedCriteria: boolean) => void;
  sectors: SectorCriteriaItem[];
  setSectors: (sectors: SectorCriteriaItem[]) => void;
  onGetResults: () => void;
  onCancel: () => void;
  onResetAll: () => void;
  token: string;
}

const SPAdvancedCriteriaPopup = ({
  themeColor,
  region,
  sector,
  tempCriteria,
  setTempCriteria,
  setOpenSPAdvancedCriteria,
  sectors,
  setSectors,
  onGetResults,
  onCancel,
  onResetAll,
  token,
}: SPAdvancedCriteriaPopupProps) => {
  const [backToHomePage, setBackToHomePage] = useState(
    tempCriteria.some((item) => item.selected) ? false : true,
  );
  const [openSelectCriteria, setOpenSelectCriteria] = useState(
    tempCriteria.some((item) => item.selected) ? false : true,
  );
  const [openSelectSector, setOpenSelectSector] = useState(false);

  const isAllSectorsSelected = sectors.every((item) => item.selected);
  const isSomeSectorsSelected = sectors.some((item) => item.selected);
  const selectedSectorsList = sectors.filter((item) => item.selected);
  const selectedSectorCount = selectedSectorsList.length;

  const getSectorRowLabel = () => {
    if (selectedSectorCount === 0 || isAllSectorsSelected) return 'All';
    if (selectedSectorCount === 1) return selectedSectorsList[0].label;
    return `Sectors (${selectedSectorCount})`;
  };

  const toggleAllSectors = () => {
    setSectors(
      sectors.map((item) => ({ ...item, selected: !isAllSectorsSelected })),
    );
  };

  const toggleSector = (value: string) => {
    setSectors(
      sectors.map((item) =>
        item.value === value ? { ...item, selected: !item.selected } : item,
      ),
    );
  };

  // Check if any criteria are still loading min/max data
  const hasPendingMinMax = tempCriteria.some(
    (item: Criteria) => item.pendingMinMax === true,
  );

  const renderScreenerCriteria = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflow: 'auto',
        }}
      >
        <Box
          sx={{
            flex: 1,
            padding: '0px 24px 16px 24px',
            overflow: 'auto',
          }}
        >
          <Box sx={{ padding: '16px 0' }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '16px',
                lineHeight: '24px',
                color: 'var(--text-subheader)',
                marginBottom: '8px',
              }}
            >
              Sector
            </Typography>
            <Box
              onClick={() => setOpenSelectSector(true)}
              sx={{
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: '40px',
                padding: '8px 12px',
                border: '1px solid var(--input-border)',
                borderRadius: '4px',
                cursor: 'pointer',
                '&:hover': {
                  border: '1px solid var(--border-primary-brand)',
                },
              }}
            >
              <span
                className="body-m-regular"
                style={{ color: 'var(--text-primary)' }}
              >
                {getSectorRowLabel()}
              </span>
              <ExpandIcon className="sp-advanced-expand-icon" />
            </Box>
          </Box>
          <CriteriaDetailList
            region={region}
            sector={sector}
            tempCriteria={tempCriteria}
            selectedCriteria={tempCriteria.filter((item) => item.selected)}
            setCriteria={setTempCriteria}
            token={token}
          />
        </Box>
        <Box
          sx={{
            boxSizing: 'border-box',
            padding: '16px 24px',
            height: '75px',
            marginTop: 'auto',
          }}
        >
          <Button
            sx={{
              boxSizing: 'border-box',
              width: '100%',
              height: '48px',
              padding: '12px 16px',
              backgroundColor: '#FFFFFF',
              border: `1px solid var(--color-brand)`,
              fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
              fontWeight: '700',
              fontSize: '16px',
              lineHeight: '24px',
              color: 'var(--color-brand)',
              boxShadow: 'none',
              textTransform: 'none',
              transition: 'none',
              '&.MuiButton-text': {
                color: 'var(--color-brand)',
                backgroundColor: '#FFFFFF',
              },
              '&:hover': {
                background: 'var(--color-brand-hover2)',
              },
            }}
            onClick={() => setOpenSelectCriteria(true)}
          >
            Add a criteria
          </Button>
        </Box>
        <Box
          sx={{
            boxSizing: 'border-box',
            height: '224px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '24px',
            borderTop: '1px solid #E0E0E0',
            marginTop: 'auto',
          }}
        >
          <Button
            sx={{
              boxSizing: 'border-box',
              width: '100%',
              height: '48px',
              padding: '12px 16px',
              backgroundColor: themeColor,
              fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
              fontWeight: '700',
              fontSize: '16px',
              lineHeight: '24px',
              color: '#FFFFFF',
              boxShadow: 'none',
              textTransform: 'none',
              '&:hover': {
                opacity: 0.9,
              },
            }}
            color="primary"
            disabled={hasPendingMinMax}
            onClick={onGetResults}
          >
            Get Results
          </Button>
          <Button
            sx={{
              boxSizing: 'border-box',
              width: '100%',
              height: '48px',
              padding: '12px 16px',
              backgroundColor: 'transparent',
              fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
              fontWeight: '700',
              fontSize: '16px',
              lineHeight: '24px',
              color: 'var(--color-brand)',
              boxShadow: 'none',
              textTransform: 'none',
              '&:hover': {
                background: 'var(--color-brand-hover2)',
              },
            }}
            color="primary"
            onClick={onResetAll}
          >
            Reset All
          </Button>
          <Button
            sx={{
              boxSizing: 'border-box',
              width: '100%',
              height: '48px',
              padding: '12px 16px',
              backgroundColor: 'transparent',
              fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
              fontWeight: '700',
              fontSize: '16px',
              lineHeight: '24px',
              color: 'var(--color-brand)',
              boxShadow: 'none',
              textTransform: 'none',
              '&:hover': {
                background: 'var(--color-brand-hover2)',
              },
            }}
            color="primary"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    );
  };

  const renderSelectCriteria = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflow: 'auto',
        }}
      >
        <Box
          sx={{
            flex: 1,
            padding: '16px 24px',
            overflow: 'auto',
          }}
        >
          <ScreenerCriteriaSelect
            criteria={tempCriteria}
            setCriteria={setTempCriteria}
            groups={AC_GROUPS}
          />
        </Box>
        <Box
          sx={{
            boxSizing: 'border-box',
            height: '160px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '24px',
            borderTop: '1px solid #E0E0E0',
            marginTop: 'auto',
          }}
        >
          <Button
            sx={{
              boxSizing: 'border-box',
              width: '100%',
              height: '48px',
              padding: '12px 16px',
              backgroundColor: themeColor,
              fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
              fontWeight: '700',
              fontSize: '16px',
              lineHeight: '24px',
              color: '#FFFFFF',
              boxShadow: 'none',
              textTransform: 'none',
              transition: 'none',
              '&.MuiButton-text': {
                color: '#FFFFFF',
                backgroundColor: themeColor,
              },
              '&:hover': {
                opacity: 0.9,
              },
            }}
            onClick={() => {
              setOpenSelectCriteria(false);
              setBackToHomePage(false);
            }}
          >
            {`Add Selected Criteria (${tempCriteria.filter((item) => item.selected).length})`}
          </Button>
          <Button
            sx={{
              boxSizing: 'border-box',
              width: '100%',
              height: '48px',
              padding: '12px 16px',
              backgroundColor: 'transparent',
              fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
              fontWeight: '700',
              fontSize: '16px',
              lineHeight: '24px',
              color: 'var(--color-brand)',
              boxShadow: 'none',
              textTransform: 'none',
              '&:hover': {
                background: 'var(--color-brand-hover2)',
              },
            }}
            color="primary"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    );
  };

  const renderSectorRow = (
    label: string,
    checked: boolean,
    indeterminate: boolean,
    onClick: () => void,
    key?: string,
  ) => (
    <Box
      key={key}
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        height: '48px',
        cursor: 'pointer',
        '& svg': {
          width: '24px',
          height: '24px',
          flexShrink: 0,
          color: 'var(--color-brand)',
        },
        '&:hover': {
          backgroundColor: 'var(--color-brand-hover2)',
        },
        ...(checked && {
          '& svg path:first-of-type': {
            fill: 'var(--color-brand)',
          },
        }),
      }}
    >
      {indeterminate ? (
        <CheckboxIndeterminateIcon />
      ) : checked ? (
        <CheckboxIcon />
      ) : (
        <CheckboxBlankIcon />
      )}
      <Typography
        sx={{
          fontSize: '16px',
          lineHeight: '24px',
          color: 'var(--text-primary)',
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  const renderSelectSector = () => {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflow: 'auto',
        }}
      >
        <Box
          sx={{
            flex: 1,
            padding: '8px 24px 16px 24px',
            overflow: 'auto',
          }}
        >
          {renderSectorRow(
            'All',
            isAllSectorsSelected,
            isSomeSectorsSelected && !isAllSectorsSelected,
            toggleAllSectors,
          )}
          {sectors.map((option) =>
            renderSectorRow(
              option.label,
              option.selected,
              false,
              () => toggleSector(option.value),
              option.value,
            ),
          )}
        </Box>
        <Box
          sx={{
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            padding: '24px',
            borderTop: '1px solid #E0E0E0',
            marginTop: 'auto',
          }}
        >
          <Button
            sx={{
              boxSizing: 'border-box',
              width: '100%',
              height: '48px',
              padding: '12px 16px',
              backgroundColor: 'transparent',
              fontFamily: "'Arial', 'Helvetica', 'Sans-serif'",
              fontWeight: '700',
              fontSize: '16px',
              lineHeight: '24px',
              color: 'var(--color-brand)',
              boxShadow: 'none',
              textTransform: 'none',
              '&:hover': {
                background: 'var(--color-brand-hover2)',
              },
            }}
            color="primary"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        backgroundColor: '#FFFFFF',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
            height: '50px',
            padding: '16px',
            position: 'relative',
            borderBottom: '1px solid #BDBDBD',
          }}
        >
          <IconButton
            sx={{
              padding: '0px',
              position: 'absolute',
              left: '16px',
              '& svg': {
                width: '24px',
                height: '24px',
              },
            }}
            aria-label="Back"
            onClick={() =>
              openSelectSector
                ? setOpenSelectSector(false)
                : openSelectCriteria
                  ? backToHomePage
                    ? setOpenSPAdvancedCriteria(false)
                    : setOpenSelectCriteria(false)
                  : setOpenSPAdvancedCriteria(false)
            }
          >
            <ArrowLeftIcon />
          </IconButton>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 700,
              lineHeight: '24px',
              color: '#1E1F2F',
              textAlign: 'center',
            }}
          >
            {openSelectSector
              ? 'Select sector'
              : openSelectCriteria
                ? 'Select Criteria'
                : 'Screener Criteria'}
          </Typography>
        </Box>

        {openSelectSector
          ? renderSelectSector()
          : openSelectCriteria
            ? renderSelectCriteria()
            : renderScreenerCriteria()}
      </Box>
    </Box>
  );
};

export default SPAdvancedCriteriaPopup;
